import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Chip,
  Avatar,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  AccountBalance,
  CreditCard,
  QrCode,
  ContentCopy,
  Close,
} from "@mui/icons-material";


interface Props {
  open: boolean;
  onClose: () => void;
  account: BankAccount;
}

const BankAccountDetailDialog: React.FC<Props> = ({ open, onClose, account }) => {
  const handleCopyAccountNumber = () => {
    copyToClipboard(account.account_number);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: "linear-gradient(to bottom, #f5f7fa 0%, #e4e8ed 100%)",
        },
      }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: "primary.main" }}>
            <AccountBalance />
          </Avatar>
          <Typography variant="h5" fontWeight="bold">
            {account.bank_name} Account Details
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={4}>
          {/* Left Column - Account Information */}
          <Box flex={1}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Account Information
              </Typography>
              <Divider sx={{ my: 2 }} />

              <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Bank Name
                  </Typography>
                  <Typography variant="body1">{account.bank_name}</Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Account Number
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CreditCard fontSize="small" color="action" />
                    <Typography variant="body1" fontFamily="monospace">
                      {account.account_number}
                    </Typography>
                    <Tooltip title="Copy to clipboard">
                      <IconButton
                        size="small"
                        onClick={handleCopyAccountNumber}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

              </Box>
            </Paper>
          </Box>

          {/* Right Column - QR Code */}
          <Box
            flex={1}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                fontWeight="bold"
                sx={{ alignSelf: "flex-start" }}
              >
                QR Code
              </Typography>
              <Divider sx={{ my: 2, width: "100%" }} />

              {account.qr_code ? (
                <>
                  <Box
                    component="img"
                    src={account.qr_code}
                    alt="Bank QR Code"
                    sx={{
                      width: 250,
                      height: 250,
                      objectFit: "contain",
                      border: "1px solid #e0e0e0",
                      borderRadius: 2,
                      p: 1,
                      mb: 2,
                    }}
                  />
                
                </>
              ) : (
                <Box
                  sx={{
                    width: 250,
                    height: 250,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px dashed #e0e0e0",
                    borderRadius: 2,
                    mb: 2,
                  }}
                >
                  <Typography color="text.secondary">
                    No QR Code Available
                  </Typography>
                </Box>
              )}

              <Typography
                variant="caption"
                color="text.secondary"
                textAlign="center"
                mt={2}
              >
                {account.bank_name} • {account.account_number}
              </Typography>
            </Paper>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ borderRadius: 2, px: 3 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BankAccountDetailDialog;

export const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      // Optional: Add toast notification
    }).catch(err => {
      console.error('Failed to copy text: ', err);
    });
  };
